/**
 * SonmiIntroduction — Prototype 2
 * Route: /prototypes/sonmi-introduction
 *
 * A framework for the introduction, not the introduction itself. Presence
 * lives on the canvas across chapters — it does not get replaced, only
 * repositioned. Chapters advance by continuous transitions of *text* and
 * embedded *demo panels*, not by dissolves between full slides.
 *
 * The framework supports:
 *   • a single Presence anchor, always the same substance
 *   • one chapter statement at a time (headline + supporting line)
 *   • an optional demo panel (video / interactive placeholder)
 *   • chapter navigation as almost-invisible dots + keyboard/arrow control
 *   • narration cue text (a soft "…" — mocked; would be voice in final)
 */
import React, { useCallback, useEffect, useState } from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";
import { ChevronRight, ChevronLeft } from "lucide-react";

const CHAPTERS = [
  {
    key: "opening",
    eyebrow: "One",
    statement: "The people we serve.",
    body: "Toto exists for the people who make Rainbow work — the guides, therapists, and managers who hold the operating rhythm every day.",
    demo: null,
  },
  {
    key: "listening",
    eyebrow: "Two",
    statement: "How Sonmi listens.",
    body: "Sonmi doesn't observe the boutiques. It listens to what each person is trying to say — through the reports they upload, the choices they make, the exceptions they surface.",
    demo: { kind: "video-placeholder", label: "Prerecorded — a report arrives, Sonmi understands, waits for confirmation." },
  },
  {
    key: "understanding",
    eyebrow: "Three",
    statement: "Understanding before action.",
    body: "Before Sonmi changes anything, it shows what it believes. If the belief is wrong, nothing is committed. If the belief is right, one word from you moves the work forward.",
    demo: { kind: "interactive-placeholder", label: "Live — Understanding Review card (payroll)." },
  },
  {
    key: "closing",
    eyebrow: "Four",
    statement: "What we're going to do together.",
    body: "You'll see three things next: how the team prepares payroll, how leadership sees the boutiques, and how Toto quietly stays with you between decisions.",
    demo: null,
  },
];

export default function SonmiIntroduction() {
  const [i, setI] = useState(0);
  const chapter = CHAPTERS[i];

  const next = useCallback(() => setI((v) => Math.min(CHAPTERS.length - 1, v + 1)), []);
  const prev = useCallback(() => setI((v) => Math.max(0, v - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const notes = {
    communicates:
      "This is the *framework* Sonmi's introduction lives inside — chapters, narration, embedded demos, continuous transitions. Not the introduction itself. It should feel like one continuous surface where ideas arrive, are held, and give way to the next, with Presence as the constant.",
    placeholder: [
      "Chapter statements and bodies are placeholder copy. Final text is a writing task, not a design task.",
      "Demo slots show a labelled rectangle. In final: real prerecorded clips and one live interactive Understanding Review.",
      "Narration cue is silent here (…). In final: voiced narration in Toto's voice.",
    ],
    decisions: [
      "How many chapters? Currently four. Could be three (tight) or five (breathing).",
      "Should the demo appear beside the statement, or below in a second beat? Currently: below, revealed after a pause.",
      "Should chapter transitions be forward-only, or should Katrina be able to walk back? Currently: bidirectional (arrow keys). Erring on trust.",
    ],
    assumptions: [
      "Presence stays in the same anchor throughout — no cinematic camera work. Chapters change; substance does not.",
      "The dots are barely visible on purpose. This is a conversation, not a webinar.",
      "Keyboard arrows advance chapters. In final delivery: probably automatic pacing tied to narration length.",
    ],
  };

  return (
    <PrototypeShell title="Prototype 02 · Sonmi Introduction Framework" notes={notes} tone="dark">
      <section
        className="min-h-[calc(100vh-6rem)] flex items-center relative overflow-hidden"
        data-testid="sonmi-introduction"
      >
        {/* Presence anchor — always the same substance, quietly present */}
        <div className="absolute left-8 lg:left-24 top-1/2 -translate-y-1/2 opacity-90">
          <PresenceForm size={280} tone="cool" testId="intro-presence" />
        </div>

        {/* Chapter panel */}
        <div className="w-full max-w-3xl ml-auto mr-8 lg:mr-24 pl-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500" data-testid="chapter-eyebrow">
            Chapter · {chapter.eyebrow}
          </div>
          <h2
            className="font-serif-display text-5xl lg:text-6xl leading-[1.05] text-stone-50 mt-4"
            style={{ fontWeight: 400 }}
            data-testid="chapter-statement"
          >
            {chapter.statement}
          </h2>
          <p className="text-lg text-stone-300 leading-relaxed mt-6 max-w-xl" data-testid="chapter-body">
            {chapter.body}
          </p>

          {chapter.demo && (
            <div
              className="mt-10 rounded-xl border border-stone-700/60 bg-stone-900/40 aspect-video max-w-xl flex items-center justify-center text-stone-500 text-xs italic"
              data-testid="chapter-demo"
            >
              [{chapter.demo.kind}] {chapter.demo.label}
            </div>
          )}

          <div className="mt-4 text-[11px] text-stone-500 italic" data-testid="chapter-narration">
            …&nbsp; (narration cue — silent in prototype)
          </div>
        </div>

        {/* Chapter dots + arrow controls */}
        <nav className="absolute bottom-8 inset-x-0 flex items-center justify-center gap-6" data-testid="chapter-nav">
          <button onClick={prev} disabled={i === 0} className="text-stone-500 hover:text-stone-100 disabled:opacity-20" data-testid="chapter-prev">
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-2.5">
            {CHAPTERS.map((c, idx) => (
              <button
                key={c.key}
                onClick={() => setI(idx)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${idx === i ? "bg-stone-100 w-5" : "bg-stone-600 hover:bg-stone-400"}`}
                data-testid={`chapter-dot-${idx}`}
                aria-label={`Chapter ${idx + 1}`}
              />
            ))}
          </div>
          <button onClick={next} disabled={i === CHAPTERS.length - 1} className="text-stone-500 hover:text-stone-100 disabled:opacity-20" data-testid="chapter-next">
            <ChevronRight size={16} />
          </button>
        </nav>
      </section>
    </PrototypeShell>
  );
}
